import { copyText } from '../../core/copy';
import { clear, el } from '../../core/dom';
import { makeBanner, makeChip, makeFieldHead, makePanel, setChip } from '../../core/shell';
import { analyzeEmailHeaders, type EmailAnalysis } from './parse';
import { SAMPLE_HEADERS } from './sample';

function fillPanels(panels: HTMLElement, result: EmailAnalysis): void {
	clear(panels);
	if (!result.ok) {
		panels.appendChild(
			makePanel(
				'Results',
				el('p', { className: 'kb-tools__muted' }, [
					'Paste raw headers and analyze to inspect sender fields, authentication results, and Received hops.',
				])
			)
		);
		return;
	}

	const sender = el('dl', { className: 'kb-tools__claim-list' });
	const rows: Array<[string, string | undefined]> = [
		['From', result.from],
		['Reply-To', result.replyTo],
		['Return-Path', result.returnPath],
		['Message-ID', result.messageId],
		['Date', result.date],
	];
	for (const [k, v] of rows) {
		sender.appendChild(el('dt', {}, [k]));
		sender.appendChild(el('dd', {}, [v || '—']));
	}
	panels.appendChild(makePanel('Sender-related fields', sender));

	const authBody = el('div', {});
	if (!result.auth.length) {
		authBody.appendChild(el('p', { className: 'kb-tools__muted' }, ['No Authentication-Results headers found.']));
	} else {
		const list = el('ul', { className: 'kb-tools__bullets' });
		for (const a of result.auth) {
			list.appendChild(
				el('li', {}, [`${a.mechanism}=${a.result} (reporter: ${a.reporter}) — ${a.detail}`])
			);
		}
		authBody.appendChild(list);
		authBody.appendChild(
			el('p', { className: 'kb-tools__muted' }, [
				'These assertions come from the pasted headers and are not independently verified.',
			])
		);
	}
	panels.appendChild(makePanel('Authentication results', authBody));

	const hopsBody = el('div', {});
	hopsBody.appendChild(
		el('p', { className: 'kb-tools__muted' }, [
			`Showing ${result.hops.length} Received hop(s) in header order (top = most recent).`,
		])
	);
	const hopList = el('ol', { className: 'kb-tools__hop-list' });
	for (const hop of result.hops) {
		const item = el('li', {});
		item.appendChild(
			document.createTextNode(
				`${hop.fromHost || 'unknown'} → ${hop.byHost || 'unknown'} · IP ${hop.ip || 'n/a'} · ${hop.date || 'no date'}`
			)
		);
		const raw = el('pre', { className: 'kb-tools-json kb-tools-json--compact' });
		raw.textContent = hop.raw;
		item.appendChild(raw);
		hopList.appendChild(item);
	}
	hopsBody.appendChild(hopList);
	panels.appendChild(makePanel('Received hops', hopsBody));
}

export function mountEmailHeaders(root: HTMLElement): void {
	root.classList.add('kb-tools', 'kb-tools--email');
	root.querySelectorAll('.kb-tools__ssr-fallback').forEach((n) => n.remove());

	const textarea = el('textarea', {
		className: 'kb-tools__textarea kb-tools__textarea--tall',
		id: 'kb-email-input',
		rows: '12',
		spellcheck: 'false',
		autocomplete: 'off',
		'aria-describedby': 'kb-email-status kb-email-privacy',
	}) as HTMLTextAreaElement;
	textarea.placeholder = 'From: …\nReceived: …\nAuthentication-Results: …';

	const analyzeBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--primary' }, [
		'Analyze',
	]) as HTMLButtonElement;
	const sampleBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Load sample',
	]) as HTMLButtonElement;
	const clearBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Clear',
	]) as HTMLButtonElement;
	const copyReport = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Copy report',
	]) as HTMLButtonElement;

	const compose = el('div', { className: 'kb-tools__compose' }, [
		makeFieldHead(
			el('label', { className: 'kb-tools__label', for: 'kb-email-input' }, ['Raw email headers']),
			'⌘/Ctrl + Enter'
		),
		textarea,
		el('div', { className: 'kb-tools__actions' }, [analyzeBtn, sampleBtn, clearBtn, copyReport]),
	]);

	const chip = makeChip('kb-email-status');
	const warnings = el('ul', { className: 'kb-tools__warnings' });
	const panels = el('div', { className: 'kb-tools__panels kb-tools__panels--stack' });
	const results = el('div', { className: 'kb-tools__results' }, [
		el('div', { className: 'kb-tools__status-row' }, [chip]),
		warnings,
		panels,
	]);

	let lastReport = '';
	let idle = true;

	clear(root);
	root.append(
		makeBanner('Local', 'Analysis happens in this browser; pasted headers are not uploaded.'),
		el('div', { className: 'kb-tools__workspace' }, [compose, results]),
		el('p', { className: 'kb-tools__privacy', id: 'kb-email-privacy' }, [
			'Received traces and Authentication-Results can be forged. Do not treat the earliest hop as definitive origin.',
		])
	);

	const paint = (result: EmailAnalysis): void => {
		if (idle) {
			setChip(chip, 'Waiting for headers', 'idle');
		} else if (!result.ok) {
			setChip(chip, result.status === 'too-large' ? 'Too large' : 'Need headers', 'error');
		} else {
			setChip(chip, 'Analyzed locally', 'ok');
		}
		clear(warnings);
		if (!idle) {
			for (const note of result.notes) {
				warnings.appendChild(el('li', {}, [note]));
			}
			if (!result.ok && result.message) {
				warnings.appendChild(el('li', {}, [result.message]));
			}
		}
		fillPanels(panels, idle ? { ...result, ok: false } : result);
	};

	const run = (): void => {
		idle = !textarea.value.trim();
		const result = analyzeEmailHeaders(textarea.value);
		lastReport = result.report;
		paint(result);
	};

	analyzeBtn.addEventListener('click', () => {
		idle = false;
		const result = analyzeEmailHeaders(textarea.value);
		lastReport = result.report;
		paint(result);
	});
	sampleBtn.addEventListener('click', () => {
		textarea.value = SAMPLE_HEADERS;
		idle = false;
		run();
	});
	clearBtn.addEventListener('click', () => {
		textarea.value = '';
		lastReport = '';
		idle = true;
		paint(analyzeEmailHeaders(''));
		textarea.focus();
	});
	copyReport.addEventListener('click', () => {
		if (lastReport) {
			void copyText(lastReport, copyReport);
		}
	});
	textarea.addEventListener('keydown', (event) => {
		if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
			event.preventDefault();
			analyzeBtn.click();
		}
	});

	paint(analyzeEmailHeaders(''));
}
